import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import RedeemOutlinedIcon from '@mui/icons-material/RedeemOutlined';
import { Box, Button, Divider, Skeleton, Typography, useMediaQuery, useTheme } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { AppWrapper, DrawerItem, Subtitle, Title, VendorLine, ViewDetailsSidebar } from '../../components/_mui';
import {
  ALL_SET_ICON_SX,
  ALL_SET_NOTICE_SX,
  CONTINUE_TO_SHIPPING_LABEL,
  getAvailableText,
  getNeedsYourChoiceText,
  getNoOptionsNoticeText,
  LOADING_OPTIONS_LABEL,
  NOTHING_TO_CHOOSE_TEXT,
  NO_OPTIONS_NOTICE_TITLE,
  OPTIONS_SUBTITLE_SKELETON_WIDTH,
} from '../../constants/marketplace';
import { RAIL_COLLAPSE_BREAKPOINT, SCROLL_LANDING_OFFSET_MOBILE } from '../../constants/ui';
import { useFab } from '../../hooks/useFab';
import { useFooter } from '../../hooks/useFooter';
import useWindowSize from '../../hooks/useWindowSize';
import { submitMSKUStep } from '../../store/actions/form';
import { marketplaceHydrateItemsRequestAction } from '../../store/actions/marketplace';
import { selectIsFirstStep, selectPickedCampaign } from '../../store/selectors/derived';
import { selectPickedItems } from '../../store/selectors/form';
import { selectIsEmbed, selectItems } from '../../store/selectors/init';
import {
  selectHasMarketplaceItems,
  selectHasUnresolvedMarketplaceItems,
  selectIsMarketplaceItemOutOfStockFn,
  selectIsMarketplaceItemResolvedFn,
  selectMarketplaceDescriptionFn,
  selectMarketplaceItemStatusFn,
  selectMarketplaceItemVendorFn,
  selectMarketplaceProductIdsToLoad,
} from '../../store/selectors/marketplace';
import { IPickedMskuItem } from '../../types/api';
import { ICampaignItem } from '../../types/pyg';
import { DescriptionSourceEnum, IScrollThresholds } from '../../types/shell';
import { convertInventoryItemsToPickedItems, getConvertedPathname, isSkuOptionOutOfStock } from '../../util/helpers';
import {
  addMarketplacePrefix,
  isMarketplaceId,
  isSettledWithoutOptions,
  stripMarketplacePrefix,
} from '../../util/marketplace';
import { OptionsListItem } from './components/OptionsListItem';
import { SummarySection } from './components/SummarySection';

const getItemKey = (item: ICampaignItem) => item.id;

interface ISkuOptionWithOrigin {
  option: ICampaignItem;
  isMarketplace: boolean;
}

const subtitleSx: SxProps<Theme> = { display: 'flex', alignItems: 'center', gap: 1 };
const railDividerSx: SxProps<Theme> = { mx: 5, mt: 4, borderColor: 'grey.400' };
const allSetIconGlyphSx: SxProps<Theme> = { fontSize: 32, color: 'success.main' };
const allSetTextSx: SxProps<Theme> = { display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 };
const continueIconSx: SxProps<Theme> = { fontSize: 18 };
const drawerHeaderSx: SxProps<Theme> = { display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1 };
const drawerOptionSx: SxProps<Theme> = { fontWeight: 400, fontSize: 16, lineHeight: 1.5, color: 'text.primary' };

const DOWN_THRESHOLDS: IScrollThresholds = { hideAt: 0.7, showAt: 0.85 };
const UP_THRESHOLDS: IScrollThresholds = { showAt: 0.1, hideAt: 0.25 };

const PickMsku = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const history = useHistory();
  const { mobile } = useWindowSize();
  const { pathname } = useLocation();
  // The footer-facing bits use the same breakpoint as Footer.tsx, not this page's own `mobile` flag.
  const isMobileViewport = useMediaQuery(theme.breakpoints.down('sm'));
  // Collapses to single-column at the shipping rail's breakpoint, not this page's `mobile` flag.
  const isSingleColumn = useMediaQuery(`(max-width:${RAIL_COLLAPSE_BREAKPOINT - 0.02}px)`);

  const inventoryItems = useSelector(selectItems);
  const storedPickedItems = useSelector(selectPickedItems);
  const pickedCampaign = useSelector(selectPickedCampaign);
  const isFirstStep = useSelector(selectIsFirstStep(getConvertedPathname(pathname)));
  const isEmbed = useSelector(selectIsEmbed);
  const itemStatusOf = useSelector(selectMarketplaceItemStatusFn);
  const isItemResolved = useSelector(selectIsMarketplaceItemResolvedFn);
  const itemOutOfStockOf = useSelector(selectIsMarketplaceItemOutOfStockFn);
  const itemVendorFn = useSelector(selectMarketplaceItemVendorFn);
  const descriptionOf = useSelector(selectMarketplaceDescriptionFn);
  const hasUnresolvedMarketplaceItems = useSelector(selectHasUnresolvedMarketplaceItems);
  const isMarketplaceSend = useSelector(selectHasMarketplaceItems);
  const productIdsToLoad = useSelector(selectMarketplaceProductIdsToLoad);

  const [expandedItem, setExpandedItem] = useState('');
  const [pickedItems, setPickedItems] = useState<Record<string, IPickedMskuItem>>(
    storedPickedItems && Object.keys(storedPickedItems).length
      ? { ...storedPickedItems }
      : convertInventoryItemsToPickedItems(inventoryItems || []),
  );
  const pickedItemsRef = useRef(pickedItems);
  pickedItemsRef.current = pickedItems;
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState<ICampaignItem | undefined>(undefined);
  const optionsSectionRef = useRef<HTMLDivElement>(null);
  const itemHeaderRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const pickedValues = useMemo(() => Object.values(pickedItems), [pickedItems]);

  const mskuItemsList = useMemo(() => {
    return inventoryItems?.filter((item) => item.sku_options && item.sku_options.length > 0);
  }, [inventoryItems]);

  // Every marketplace item starts in the options list: whether it needs a pick is only known once
  // its details land. A settled single-variant item moves to Included items.
  const isSettledWithoutOptionsOf = useCallback(
    (item: ICampaignItem) => isSettledWithoutOptions(item, isItemResolved(item.id)),
    [isItemResolved],
  );

  const isOutOfStockOf = useCallback((item: ICampaignItem) => itemOutOfStockOf(item.id), [itemOutOfStockOf]);

  const isIncludedItem = useCallback(
    (item: ICampaignItem) => isSettledWithoutOptionsOf(item) && !isOutOfStockOf(item),
    [isSettledWithoutOptionsOf, isOutOfStockOf],
  );

  const optionsListItems = useMemo(() => {
    if (!isMarketplaceSend) {
      return mskuItemsList;
    }

    return inventoryItems?.filter((item) => !isIncludedItem(item));
  }, [isMarketplaceSend, inventoryItems, mskuItemsList, isIncludedItem]);

  const fixedItemsList = useMemo(() => {
    if (isMarketplaceSend) {
      return inventoryItems?.filter(isIncludedItem);
    }

    return inventoryItems?.filter((item) => !item.sku_options || item.sku_options.length === 0);
  }, [isMarketplaceSend, inventoryItems, isIncludedItem]);

  const choosableItemsList = useMemo(
    () => mskuItemsList?.filter((item) => !isOutOfStockOf(item)),
    [mskuItemsList, isOutOfStockOf],
  );

  const handleSelectItem = useCallback(
    (internalId: string, skuOption: IPickedMskuItem) => {
      const nextPicks = { ...pickedItems, [internalId]: skuOption };

      setPickedItems(nextPicks);
      setExpandedItem(choosableItemsList?.find((item) => !nextPicks[item.internalId]?.uid)?.internalId ?? '');
    },
    [pickedItems, choosableItemsList],
  );

  const choosableItemsKey = (choosableItemsList ?? []).map((item) => item.internalId).join('|');
  const choosableItemsRef = useRef(choosableItemsList);
  choosableItemsRef.current = choosableItemsList;

  const nextUnfinishedItem = useMemo(
    () => choosableItemsList?.find((item) => !pickedItems[item.internalId]?.uid),
    [choosableItemsList, pickedItems],
  );

  const nextUnfinishedHeaderRef = useRef<HTMLDivElement | null>(null);
  // Re-syncs on `mskuItemsList` too, so it repoints once the header rows mount.
  useEffect(() => {
    nextUnfinishedHeaderRef.current = nextUnfinishedItem
      ? itemHeaderRefs.current[nextUnfinishedItem.internalId] ?? null
      : null;
  }, [nextUnfinishedItem, choosableItemsList]);

  const registerHeaderRef = useCallback(
    (internalId: string, el: HTMLDivElement | null) => {
      itemHeaderRefs.current[internalId] = el;
      if (nextUnfinishedItem?.internalId === internalId) {
        nextUnfinishedHeaderRef.current = el;
      }
    },
    [nextUnfinishedItem],
  );

  const scrollToItemHeader = useCallback((internalId: string, behavior: ScrollBehavior) => {
    const header = itemHeaderRefs.current[internalId];
    if (!header) {
      return;
    }
    const top = header.getBoundingClientRect().top + window.scrollY;

    window.scrollTo({ top: top - SCROLL_LANDING_OFFSET_MOBILE, behavior });
  }, []);

  const fabConfig = useMemo(
    () => ({
      desktopFabEnabled: false,
      downAnchorRef: nextUnfinishedHeaderRef,
      downThresholds: DOWN_THRESHOLDS,
      upThresholds: UP_THRESHOLDS,
      down: nextUnfinishedItem
        ? {
            label: `Choose ${nextUnfinishedItem.name.toLowerCase()}`,
            onActivate: (behavior: ScrollBehavior) => {
              const { internalId } = nextUnfinishedItem;
              setExpandedItem(internalId);
              // Scroll on the next frame so the expanded header's layout is measured.
              requestAnimationFrame(() => scrollToItemHeader(internalId, behavior));
            },
          }
        : undefined,
      up: {
        label: 'Back to top',
        onActivate: (behavior: ScrollBehavior) => window.scrollTo({ top: 0, behavior }),
      },
    }),
    [nextUnfinishedItem, scrollToItemHeader],
  );

  useFab(fabConfig);

  useEffect(() => {
    const items = choosableItemsRef.current;

    if (!items || hasUnresolvedMarketplaceItems) {
      return;
    }

    setExpandedItem(items.find((item) => !pickedItemsRef.current[item.internalId]?.uid)?.internalId ?? '');
  }, [choosableItemsKey, hasUnresolvedMarketplaceItems]);

  const handleContinue = useCallback(
    () =>
      new Promise<string>((resolve) => {
        dispatch(submitMSKUStep({ pickedItems, resolve }));
      }).then(history.push),
    [dispatch, history, pickedItems],
  );

  const handleContinueClick = useCallback(() => {
    if (!isEmbed) {
      handleContinue();
    }
  }, [isEmbed, handleContinue]);

  const handleBack = useCallback(() => history.goBack(), [history]);

  const handleRetryDetails = useCallback(
    (productId: string) => dispatch(marketplaceHydrateItemsRequestAction({ productIds: [productId] })),
    [dispatch],
  );

  useEffect(() => {
    if (productIdsToLoad.length) {
      dispatch(marketplaceHydrateItemsRequestAction({ productIds: productIdsToLoad }));
    }
  }, [dispatch, productIdsToLoad]);

  const [selectFromDrawer, setSelectFromDrawer] = useState<(() => void) | undefined>(undefined);

  const marketplaceProductIdOf = useCallback(
    (clicked: ICampaignItem): string | null => {
      if (isMarketplaceId(clicked.id)) {
        return stripMarketplacePrefix(clicked.id);
      }

      const owner = inventoryItems?.find((item) => item.sku_options?.some((option) => option.id === clicked.id));

      return owner && isMarketplaceId(owner.id) ? stripMarketplacePrefix(owner.id) : null;
    },
    [inventoryItems],
  );

  const handleViewDetails = useCallback(
    (item: ICampaignItem, onSelectOption?: () => void) => {
      const productId = marketplaceProductIdOf(item);

      if (productId) {
        dispatch(marketplaceHydrateItemsRequestAction({ productIds: [productId] }));
      }
      setCurrentItem(item);
      setSelectFromDrawer(() => onSelectOption);
      setIsDrawerOpen(true);
    },
    [dispatch, marketplaceProductIdOf],
  );

  const handleDetailsClose = useCallback(() => {
    setIsDrawerOpen(false);
    setCurrentItem(undefined);
    setSelectFromDrawer(undefined);
  }, []);

  const handleSelectFromDrawer = useCallback(() => {
    selectFromDrawer?.();
    handleDetailsClose();
  }, [selectFromDrawer, handleDetailsClose]);

  // A card still loading may yet reveal variants that need picking, and a failed card would
  // submit a claim with no variant chosen.
  const isStepDisabled = useMemo(() => {
    if (hasUnresolvedMarketplaceItems) {
      return true;
    }

    if (!choosableItemsList?.length) {
      return !fixedItemsList?.length;
    }

    return !choosableItemsList.every((item) => !!pickedItems[item.internalId]?.uid);
  }, [hasUnresolvedMarketplaceItems, choosableItemsList, fixedItemsList, pickedItems]);

  useEffect(() => {
    if (choosableItemsList && choosableItemsList.length > 0 && !expandedItem) {
      setExpandedItem(choosableItemsList[0].internalId);
    }
  }, []);

  useEffect(() => {
    if (!inventoryItems?.length) {
      return;
    }

    setPickedItems((existing) => {
      const seeded = convertInventoryItemsToPickedItems(inventoryItems);
      const missingKeys = Object.keys(seeded).filter((key) => !(key in existing));

      // Return the same object when nothing is missing, or this effect re-triggers itself.
      return missingKeys.length
        ? { ...existing, ...missingKeys.reduce((acc, key) => ({ ...acc, [key]: null }), {}) }
        : existing;
    });
  }, [inventoryItems]);

  const hasChoosableItems = !!choosableItemsList?.length;
  const hasIncludedItems = !!fixedItemsList?.length;

  const isSelectionRailHidden =
    isMarketplaceSend && (hasUnresolvedMarketplaceItems || (!hasChoosableItems && !hasIncludedItems));
  const isRailDividerShown = !isSingleColumn && !isSelectionRailHidden;

  const mskuItemsCount = choosableItemsList?.length || 0;

  const mskuItemsPickedCount = useMemo(() => {
    return pickedValues.filter((item) => item?.uid).length;
  }, [pickedValues]);

  const outOfStockItems = useMemo(() => {
    const skuOptions = (inventoryItems ?? [])
      .flatMap((item) =>
        (item.sku_options ?? []).map((option) => ({ option, isMarketplace: isMarketplaceId(item.id) })),
      )
      .reduce((acc, current) => {
        if (!acc.find(({ option }) => option.id === current.option.id)) {
          acc.push(current);
        }

        return acc;
      }, [] as ISkuOptionWithOrigin[]);

    return skuOptions
      .filter(({ option, isMarketplace }) => {
        const quantity = pickedValues.reduce(
          (total, currentValue) => (currentValue?.uid === option.id ? total + 1 : total),
          0,
        );

        return isSkuOptionOutOfStock(option, quantity, isMarketplace);
      })
      .map(({ option }) => option.id);
  }, [inventoryItems, pickedValues]);

  const isCurrentItemSelected = useMemo(
    () => !!currentItem && pickedValues.some((picked) => picked?.uid === currentItem.id),
    [currentItem, pickedValues],
  );

  const drawerButton = useMemo(() => {
    const canSelect = !!selectFromDrawer && !isCurrentItemSelected;

    return (
      <Button
        variant="contained"
        color="primary"
        onClick={canSelect ? handleSelectFromDrawer : handleDetailsClose}
        fullWidth={mobile}
      >
        {canSelect ? 'Select this option' : 'Close'}
      </Button>
    );
  }, [selectFromDrawer, isCurrentItemSelected, handleSelectFromDrawer, handleDetailsClose, mobile]);

  const showBackButton = !isFirstStep && !isMobileViewport;

  const backFooterButton = useMemo(() => {
    return showBackButton ? (
      <Button variant="outlined" color="primary" onClick={handleBack} startIcon={<ChevronLeftIcon />}>
        Back
      </Button>
    ) : undefined;
  }, [showBackButton, handleBack]);

  const continueFooterButton = useMemo(() => {
    return (
      <Button
        variant="contained"
        color="primary"
        onClick={handleContinueClick}
        disabled={isStepDisabled}
        endIcon={<ChevronRightIcon sx={continueIconSx} />}
        fullWidth={isMobileViewport}
        size="medium"
      >
        {CONTINUE_TO_SHIPPING_LABEL}
      </Button>
    );
  }, [handleContinueClick, isStepDisabled, isMobileViewport]);

  const remainingCount = mskuItemsCount ? mskuItemsCount - mskuItemsPickedCount : 0;

  const footerText = useMemo(() => {
    if (hasUnresolvedMarketplaceItems) {
      return (
        <Typography variant={isMobileViewport ? 'body2' : 'body1'} color="text.secondary">
          Loading your options
        </Typography>
      );
    }

    if (remainingCount <= 0) {
      return undefined;
    }

    const noun = mskuItemsCount === 1 ? 'selection' : 'selections';

    return (
      <Typography variant={isMobileViewport ? 'body2' : 'body1'} color="text.secondary">
        {`Complete ${remainingCount} of ${mskuItemsCount} ${noun} to continue`}
      </Typography>
    );
  }, [remainingCount, mskuItemsCount, isMobileViewport, hasUnresolvedMarketplaceItems]);

  const optionsSubtitle = useMemo(() => {
    if (!isMarketplaceSend) {
      return pickedCampaign?.name ? 'Select options for each item below' : 'Choose one option for each item below';
    }

    const total = (optionsListItems?.length ?? 0) + (fixedItemsList?.length ?? 0);
    const soldOutCount = optionsListItems?.filter(isOutOfStockOf).length ?? 0;
    const choiceCount = choosableItemsList?.length ?? 0;
    const choiceText = choiceCount ? getNeedsYourChoiceText(choiceCount) : NOTHING_TO_CHOOSE_TEXT;

    return `${getAvailableText(total - soldOutCount, total)} · ${choiceText}`;
  }, [isMarketplaceSend, pickedCampaign, optionsListItems, fixedItemsList, choosableItemsList, isOutOfStockOf]);

  // Nothing needed a choice: the emptied column gets a confirmation so the recipient sees the
  // check actually finished.
  const allSetNotice = useMemo(() => {
    const isEverythingIncluded =
      isMarketplaceSend && !hasUnresolvedMarketplaceItems && !hasChoosableItems && !!fixedItemsList?.length;

    if (!isEverythingIncluded) {
      return null;
    }

    return (
      <Box role="status" sx={ALL_SET_NOTICE_SX}>
        <Box sx={ALL_SET_ICON_SX}>
          <RedeemOutlinedIcon sx={allSetIconGlyphSx} />
        </Box>
        <Box sx={allSetTextSx}>
          <Typography variant="h6" fontWeight="medium">
            {NO_OPTIONS_NOTICE_TITLE}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {getNoOptionsNoticeText(fixedItemsList.length)}
          </Typography>
        </Box>
      </Box>
    );
  }, [isMarketplaceSend, hasUnresolvedMarketplaceItems, hasChoosableItems, fixedItemsList]);

  const footerConfig = useMemo(
    () => ({
      backButton: backFooterButton,
      helperText: footerText,
      submitButton: continueFooterButton,
    }),
    [footerText, continueFooterButton, backFooterButton],
  );

  useFooter(footerConfig, optionsSectionRef);

  const drawerProductId = useMemo(
    () => (currentItem ? marketplaceProductIdOf(currentItem) : null),
    [currentItem, marketplaceProductIdOf],
  );

  const drawerVendor = drawerProductId ? itemVendorFn(addMarketplacePrefix(drawerProductId)) : undefined;
  const drawerDescription = drawerProductId ? descriptionOf(drawerProductId) : undefined;
  const drawerDescriptionSource = drawerProductId ? DescriptionSourceEnum.Marketplace : DescriptionSourceEnum.Inventory;

  const drawerOwner = useMemo(
    () =>
      currentItem
        ? inventoryItems?.find((item) => item.sku_options?.some((option) => option.id === currentItem.id)) ??
          currentItem
        : undefined,
    [currentItem, inventoryItems],
  );

  const drawerOptionName = drawerOwner && drawerOwner !== currentItem ? currentItem?.name : undefined;

  const drawerHeaderContent = useMemo(
    () =>
      drawerOptionName || drawerVendor ? (
        <Box sx={drawerHeaderSx}>
          {drawerOptionName && <Typography sx={drawerOptionSx}>{drawerOptionName}</Typography>}
          <VendorLine vendor={drawerVendor} />
        </Box>
      ) : undefined,
    [drawerOptionName, drawerVendor],
  );

  const renderDrawerItem = useCallback(
    (item: ICampaignItem) => (
      <DrawerItem item={item} simple description={drawerDescription} descriptionSource={drawerDescriptionSource} />
    ),
    [drawerDescription, drawerDescriptionSource],
  );

  const appWrapperSx = useMemo<SxProps<Theme>>(
    () => (isSingleColumn ? { flexDirection: 'column', pb: 2 } : { flexDirection: 'row' }),
    [isSingleColumn],
  );

  const optionsColumnSx = useMemo<SxProps<Theme>>(
    () => ({ display: 'flex', flexDirection: 'column', ...(!isSingleColumn && { flex: 1 }) }),
    [isSingleColumn],
  );

  const optionsListSx = useMemo<SxProps<Theme>>(
    () => ({
      display: 'flex',
      flexDirection: 'column',
      gap: isSingleColumn ? 2 : 3,
      mt: allSetNotice ? 0 : 1,
    }),
    [isSingleColumn, allSetNotice],
  );

  const drawerItems = useMemo(() => (currentItem ? [currentItem] : undefined), [currentItem]);

  return (
    <>
      <AppWrapper ref={optionsSectionRef} sx={appWrapperSx}>
        <Box sx={optionsColumnSx}>
          <Title>{pickedCampaign?.name || 'Select options'}</Title>
          {!allSetNotice && (
            <Subtitle sx={subtitleSx}>
              {hasUnresolvedMarketplaceItems ? (
                <Skeleton
                  variant="text"
                  width={OPTIONS_SUBTITLE_SKELETON_WIDTH}
                  role="status"
                  aria-label={LOADING_OPTIONS_LABEL}
                />
              ) : (
                optionsSubtitle
              )}
            </Subtitle>
          )}

          <Box sx={optionsListSx}>
            {optionsListItems?.map((item: ICampaignItem) => (
              <OptionsListItem
                key={item.internalId}
                item={item}
                vendor={itemVendorFn(item.id)}
                detailsStatus={itemStatusOf(item.id)}
                isMarketplaceSend={isMarketplaceSend}
                isOutOfStock={isOutOfStockOf(item)}
                isExpanded={expandedItem === item.internalId}
                selected={pickedItems[item.internalId]}
                disabledItems={outOfStockItems}
                onExpand={setExpandedItem}
                onSelect={handleSelectItem}
                onViewDetails={handleViewDetails}
                onRetry={handleRetryDetails}
                registerHeaderRef={registerHeaderRef}
              />
            ))}
            {allSetNotice}
          </Box>
        </Box>
        {isRailDividerShown && <Divider orientation="vertical" flexItem sx={railDividerSx} />}
        {!isSelectionRailHidden && (
          <SummarySection
            pickedCount={mskuItemsPickedCount}
            itemsCount={mskuItemsCount}
            mskuList={choosableItemsList}
            fixedItemsList={fixedItemsList}
            pickedItems={pickedItems}
            handleViewDetails={handleViewDetails}
            handleOpenAccordion={setExpandedItem}
            vendorOf={itemVendorFn}
            isSingleColumn={isSingleColumn}
          />
        )}
      </AppWrapper>

      <ViewDetailsSidebar<ICampaignItem>
        open={isDrawerOpen}
        onClose={handleDetailsClose}
        title={drawerOwner?.name}
        items={drawerItems}
        renderItem={renderDrawerItem}
        getItemKey={getItemKey}
        footerButton={drawerButton}
        headerContent={drawerHeaderContent}
        hideItemsCount
      />
    </>
  );
};

export default PickMsku;
